import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

// material-ui
import { styled, useTheme } from "@mui/material/styles";
import {
  AppBar,
  Box,
  CircularProgress,
  CssBaseline,
  Toolbar,
  useMediaQuery,
} from "@mui/material";

// project imports
import Header from "./Header";
import Sidebar from "./Sidebar";
import { SET_MENU } from "store/actions/actionTypes";

// assets
import { path } from "routes/constant";
import { GeneralCoType } from "constants/companyTypes";
import MobileDrawer from "./Sidebar/MobileDrawer";
import { replaceCumulativeMemberYears } from "store/actions/cumulativeMembersActions";
import { replaceCumulativeSubscriberYears } from "store/actions/cumulativeSubscribersActions";
import { isEqual, debounce } from "lodash";
import { replaceNewInstallYears } from "store/actions/newInstallsActions";
import { replaceCumulativeInstallYears } from "store/actions/cumulativeInstallsActions";
import { replaceSubscriptionRevenueByYear } from "store/actions/revenue/subscriptionRevenueActions";
import { replaceSubscriptionCommissionByYears } from "store/actions/revenue/subscriptionCommissionsActions";
import {
  replaceCostOfSaleContdYears,
  replaceCostOfSaleContdYearsSecond,
} from "store/actions/cost-and-expenses/costOfSalesContdActions";
import { replaceGeneralAdministrativeExpenseYears } from "store/actions/cost-and-expenses/generalAdministrativeExpensesActions";
import { replaceSalesAndMarketingExpenseYears } from "store/actions/cost-and-expenses/salesAndMarketingExpensesActions";
import { replaceAdvertisementExpenseYears } from "store/actions/cost-and-expenses/advertisementExpensesActions";
import { replaceOtherAdvertisementAndMarketingExpenseYears } from "store/actions/cost-and-expenses/otherAdvertisementAndMarketingExpensesActions";
import { replaceFoundersAndPartnersYears } from "store/actions/hiring-plan/foundersAndPartnersActions";
import { replaceTechProductDevYears } from "store/actions/hiring-plan/techAndProductDevActions";
import { replaceSalesMarketingYears } from "store/actions/hiring-plan/salesAndMarketingActions";
import { replaceHiringPlanAverageYears } from "store/actions/hiring-plan/hiringPlanAverageActions";
import { replaceFinanceAnalyticsYears } from "store/actions/hiring-plan/financeAnalyticsActions";
import { replaceHiringPlanOtherYears } from "store/actions/hiring-plan/hiringPlanOtherActions";
import { replaceProfitAndLossMainYears } from "store/actions/profit-and-loss/mainActions";
import { replaceFreeCashFlowInitialYears } from "store/actions/free-cash-flow/mainActions";

import { useWorker } from "use-react-workers";
import {
  GeneralAdministrativeExpensesTableCalculation,
  SubscriberAcquisitionsCalculations,
  calculateAverageSalaryPerHeadCount,
  calculateEBITDAMargin_pnl_main_ebitdaMargin,
  calculateEBITDAPnl_main_ebitda,
  calculateFinanceAndAnalyticsAndOperations,
  calculateFoundersAndPartners,
  calculateGeneralAdministrativeExpense,
  calculateGeneralAdministrativeExpenses,
  calculateGrossProfitAndMargin,
  calculateMarketingExpensesAndMediaBuying,
  calculateOtherAdvertisementAndMarketingExpensesTotal,
  calculateOtherRevenues,
  calculatePnl_main_advertisementExpenses,
  calculateSalesAndMarketingExpenses,
  calculateSalesAndMarketingTeams,
  calculateSubscriptionRevenueTable,
  calculateTechAndDevelopment,
  calculateTotalCost,
  calculateTotalHeadCount,
  contentAndProductionCostsCalculation,
  cumulativeInstallTableCalculations,
  cumulativeSubscribersTableCalculation,
  otherTableCalculation,
  serverCostAndNumberOfAvgCumulativeMembersCalculation,
  setValuesFromAssumptions,
  subscriptionCommissionsTableCalculations,
} from "utils/monthly-calculation";
import { setCentralLoader } from "store/actions/LoaderActions";

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme }) => {
    return {
      ...theme.typography.mainContent,
      flex: 1,
      overflow: "hidden",
    };
  }
);

const debouncedLoadingFunc = debounce((dispatch, flag) => {
  dispatch(setCentralLoader(flag));
}, 300);

const debouncedLoading = (dispatch, flag) => {
  if (flag) {
    dispatch(setCentralLoader(flag));
  } else {
    debouncedLoadingFunc(dispatch, flag);
  }
};

const getZeroFilledMonths = (count) => {
  const limit = count || 36;
  const months = {};
  for (let i = 1; i <= limit; i += 1) {
    months[i] = 0;
  }
  return months;
};

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const newInstallWorker = useWorker(SubscriberAcquisitionsCalculations);
  const setValueFromAssumptionsWorker = useWorker(setValuesFromAssumptions);
  const calculateCumulativeInstallsTableWorker = useWorker(
    cumulativeInstallTableCalculations
  );

  const cumulativeSubscribersTableCalculationWorker = useWorker(
    cumulativeSubscribersTableCalculation
  );
  // Revenue Tab
  const calculateSubscriptionRevenueTableWorker = useWorker(
    calculateSubscriptionRevenueTable
  );

  const calculateSubscriptionCommissionsTableWorker = useWorker(
    subscriptionCommissionsTableCalculations
  );

  const calculateOtherRevenuesWorker = useWorker(calculateOtherRevenues);
  // Revenue Tab end
  // Cost And Expenses Tab Start

  const calculateContentAndProductionCostsWorker = useWorker(
    contentAndProductionCostsCalculation
  );

  const serverCostAndNumberOfAvgCumulativeMembersCalculationWroker = useWorker(
    serverCostAndNumberOfAvgCumulativeMembersCalculation
  );

  const GeneralAdministrativeExpensesTableCalculationWorker = useWorker(
    GeneralAdministrativeExpensesTableCalculation
  );
  const calculateMarketingExpensesAndMediaBuyingWorker = useWorker(
    calculateMarketingExpensesAndMediaBuying
  );

  const calculatePnl_main_advertisementExpensesWorker = useWorker(
    calculatePnl_main_advertisementExpenses
  );

  const calculateOtherAdvertisementAndMarketingExpensesTotalWorker = useWorker(
    calculateOtherAdvertisementAndMarketingExpensesTotal
  );
  // Cost And Expenses Tab end

  // Hiring Plans start
  const calculateFoundersAndPartnersWorker = useWorker(
    calculateFoundersAndPartners
  );
  const calculateTechAndDevelopmentWorker = useWorker(
    calculateTechAndDevelopment
  );

  const calculateSalesAndMarketingTeamsWorker = useWorker(
    calculateSalesAndMarketingTeams
  );

  const calculateFinanceAndAnalyticsAndOperationsWorker = useWorker(
    calculateFinanceAndAnalyticsAndOperations
  );

  const otherTableCalculationWorker = useWorker(otherTableCalculation);

  const calculateTotalHeadCountWorker = useWorker(calculateTotalHeadCount);

  const calculateTotalCostWorker = useWorker(calculateTotalCost);

  const calculateAverageSalaryPerHeadCountWorker = useWorker(
    calculateAverageSalaryPerHeadCount
  );
  const calculateGeneralAdministrativeExpensestWorker = useWorker(
    calculateGeneralAdministrativeExpenses
  );
  // HiringPlans end

  // Profit and loss start
  const calculateGrossProfitAndMarginWorker = useWorker(
    calculateGrossProfitAndMargin
  );

  const calculateGeneralAdministrativeExpenseWorker = useWorker(
    calculateGeneralAdministrativeExpense
  );
  const calculateSalesAndMarketingExpensesWorker = useWorker(
    calculateSalesAndMarketingExpenses
  );

  const calculateEBITDAPnl_main_ebitdaWorker = useWorker(
    calculateEBITDAPnl_main_ebitda
  );

  const calculateEBITDAMargin_pnl_main_ebitdaMarginWorker = useWorker(
    calculateEBITDAMargin_pnl_main_ebitdaMargin
  );

  // Profit and loss end

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));

  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };

  useEffect(() => {
    dispatch({ type: SET_MENU, opened: !matchDownMd });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMd]);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate(path.FINANCIAL_FORECAST_FREECASH_FLOW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const subscriberAcquisitionAssumptionsState = useSelector(
    (state) => state.subscriberAcquisitionAssumptions
  );

  const subscriberAcquisitionAssumptions =
    subscriberAcquisitionAssumptionsState;
  const newInstalls = useSelector((state) => state.newInstalls);
  const cumulativeMembers = useSelector((state) => state.cumulativeMembers);
  // const cumulativeSubscribers = useSelector(
  //   (state) => state.cumulativeSubscribers
  // );
  const cumulativeInstalls = useSelector((state) => state.cumulativeInstalls);
  const cumulativeSubscribers = useSelector(
    (state) => state.cumulativeSubscribers
  );
  const subscriptionRevenue = useSelector((state) => state.subscriptionRevenue);
  const subscriptionCommissions = useSelector(
    (state) => state.subscriptionCommissions
  );
  const costOfSales = useSelector((state) => state.costOfSales);
  const costOfSalesContd = useSelector((state) => state.costOfSalesContd);
  const generalAdministrativeExpenses = useSelector(
    (state) => state.generalAdministrativeExpenses
  );
  const salesAndMarketingExpenses = useSelector(
    (state) => state.salesAndMarketingExpenses
  );
  const advertisementExpenses = useSelector(
    (state) => state.advertisementExpenses
  );
  const otherAdvertisementAndMarketingExpenses = useSelector(
    (state) => state.otherAdvertisementAndMarketingExpenses
  );
  const foundersAndPartners = useSelector(
    (state) => state.hp_foundersAndPartners
  );
  const techProductDev = useSelector((state) => state.hp_techAndProductDev);
  const techAndProduct = techProductDev;
  const salesAndMarketing = useSelector((state) => state.hp_salesAndMarketing);
  const financeAndAnalytics = useSelector(
    (state) => state.hp_financeAndAnalytics
  );
  const other = useSelector((state) => state.hp_other);
  const average = useSelector((state) => state.hp_average);
  const otherRevenue = useSelector((state) => state.otherRevenue);
  const profitAndLossMain = useSelector((state) => state.pnl_main);
  const centralLoader = useSelector((state) => state.loaders.centralLoading);
  const periodicity = useSelector(
    (state) =>
      state.financialForecast.timeSeriesAssumptions["primaryPeriodicity"]
  );
  const { authState } = useSelector((state) => state.auth);
  const companyType = authState?.user?.Company?.Type;

  const { monthCount } = useSelector(
    (state) => state.financialForecast.timeSeriesAssumptions?.financialMonthsNew
  );

  //calculations useEffect start
  useEffect(() => {
    // webWorkers
    (() => {
      if (periodicity !== "Yearly") {
        debouncedLoading(dispatch, true);
        setValueFromAssumptionsWorker.postMessage(
          subscriberAcquisitionAssumptionsState
        );
        setValueFromAssumptionsWorker.onMessage(({ data }) => {
          dispatch(
            replaceCumulativeMemberYears(
              "registrationInstallToMember",
              data?.registrationInstallToMember
            )
          );
          dispatch(
            replaceCumulativeSubscriberYears(
              "subscriptionInstallToSubscriber",
              data?.subscriptionInstallToSubscriber
            )
          );
          dispatch(
            replaceCumulativeSubscriberYears(
              "churnAnnualSubscribers",
              data?.churnRateOfAnnualSubs
            )
          );
          dispatch(
            replaceCumulativeSubscriberYears(
              "churnMonthlySubscribers",
              data?.churnRateOfAnnualSubs
            )
          );
          debouncedLoading(dispatch, false);
        });
      }
    })();
  }, [
    periodicity,
    subscriberAcquisitionAssumptionsState["registration"]?.months,
    subscriberAcquisitionAssumptionsState["subscription"]?.months,
    subscriberAcquisitionAssumptionsState["churnRate"]?.months,
  ]);
  // __________________________________________________________________________

  // webWorkers New Install Table
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // New Install Table
      const staticParams = {
        keyAssumptionCostPerInstall:
          subscriberAcquisitionAssumptions["costPerInstall"], // COST_PER_INSTALL_KEY
        keyAssumptionMediaBuying:
          subscriberAcquisitionAssumptions["mediaBuying"], // MEDIA_BUYING_KEY
        keyAssumptionRegistrationInstallToMember:
          subscriberAcquisitionAssumptions["registration"], //REGISTRATION_KEY
        cumulativeMembersTableChurnFromPreviousYear: cumulativeMembers["churn"], // CHURN_KEY
        newInstallsTableOrganicInstallToPaidInstalls:
          newInstalls["paidInstalls"], // ORGANIC_INSTALL_TO_PAID_INSTALLS_KEY
        newInstallsTableOrganicInstallToMembers: newInstalls["installMembers"], // ORGANIC_INSTALL_TO_MEMBERS_KEY
      };

      const params = {
        // textFieldValues
        ...staticParams,
        // field which will calculate
        cumulativeInstallsTableInstall: cumulativeInstalls["install"], // INSTALL_KEY
        cumulativeMembersTableRegistration:
          cumulativeMembers["registrationInstallToMember"], // Registration
        cumulativeMembersTableTotal: cumulativeMembers["total"], // TOTAL_KEY
        paid: newInstalls["paid"],
        organic: newInstalls["organic"],
        newInstall: newInstalls["newInstalls"],
      };

      let sanitizedData = JSON.parse(JSON.stringify(params));
      let sanitizeStaticParams = JSON.parse(JSON.stringify(staticParams));

      (() => {
        debouncedLoading(dispatch, true);
        newInstallWorker.postMessage(sanitizedData, sanitizeStaticParams);
        newInstallWorker.onMessage(({ data }) => {
          dispatch(replaceNewInstallYears("organic", data?.organic?.months));
          dispatch(replaceNewInstallYears("paid", data?.paid?.months));
          dispatch(
            replaceNewInstallYears("newInstalls", data?.newInstall?.months)
          );
          // Cumulative Installs Table => Install
          dispatch(
            replaceCumulativeInstallYears(
              "install",
              data?.cumulativeInstallsTableInstall?.months
            )
          );
          dispatch(
            replaceCumulativeMemberYears(
              "registration",
              data?.cumulativeMembersTableRegistration?.months
            )
          );
          dispatch(
            replaceCumulativeMemberYears(
              "total",
              data?.cumulativeMembersTableTotal?.months
            )
          );
          dispatch(
            replaceNewInstallYears("organicPercentage", data?.OrganicAmounts)
          );
          dispatch(replaceNewInstallYears("paidPercentage", data?.paidAmounts));

          // Cumulative Members Table start
          // Deregistration
          dispatch(
            replaceCumulativeMemberYears(
              "deregistration",
              data?.cumulativeMembersDeregistration
            )
          );
          // webWorkers Deregistration percentage
          dispatch(
            replaceCumulativeMemberYears(
              "deregistrationPercentage",
              data?.cumulativeMembersDeregistrationPercentage
            )
          );
          // Cumulative Members
          dispatch(
            replaceCumulativeMemberYears(
              "cumulativeMembers",
              data?.cumulativeMembersAmounts
            )
          );

          // Cumulative Members Table end
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    // Organic and New Install
    subscriberAcquisitionAssumptions["costPerInstall"]?.months,
    subscriberAcquisitionAssumptions["mediaBuying"]?.months,
    subscriberAcquisitionAssumptions["registration"]?.months,
    cumulativeMembers["churn"]?.months,
    newInstalls["paidInstalls"]?.months,
    newInstalls["installMembers"]?.months,
    periodicity,
  ]);

  // __________________________________________________________________________

  // Cumulative Installs Table
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorkers  of CumulativeInstallsTable
      let saitizedCumulativeInstalls = JSON.parse(
        JSON.stringify(cumulativeInstalls)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateCumulativeInstallsTableWorker.postMessage(
          saitizedCumulativeInstalls
        );
        calculateCumulativeInstallsTableWorker.onMessage(({ data }) => {
          dispatch(
            replaceCumulativeInstallYears("total", data?.cumulativeTotal)
          );
          dispatch(
            replaceCumulativeInstallYears(
              "cumulativeInstalls",
              data?.cumulativeAppInstalls
            )
          );
          dispatch(
            replaceCumulativeInstallYears(
              "uninstallChurn",
              data?.cumulativeAppUninstallChurn
            )
          );
          dispatch(
            replaceCumulativeInstallYears(
              "uninstall",
              data?.cumulativeAppUninstall
            )
          );
        });
        debouncedLoading(dispatch, false);
      })();
    }
  }, [
    cumulativeInstalls["install"]?.months,
    cumulativeInstalls["churn"]?.months,
    periodicity,
  ]);

  // CumulativeMembersTable end

  // __________________________________________________________________________

  // CumulativeSubscribersTable start
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorkers CumulativeSubscribersTable
      let sanitizedCumulativeInstalls = JSON.parse(
        JSON.stringify(cumulativeInstalls)
      );
      let sanitizedSubscriberAcquisitionAssumptionsState = JSON.parse(
        JSON.stringify(subscriberAcquisitionAssumptionsState)
      );
      let sanitizedCumulativeSubscribers = JSON.parse(
        JSON.stringify(cumulativeSubscribers)
      );

      (() => {
        debouncedLoading(dispatch, true);
        cumulativeSubscribersTableCalculationWorker.postMessage(
          sanitizedCumulativeInstalls,
          sanitizedSubscriberAcquisitionAssumptionsState,
          sanitizedCumulativeSubscribers
        );

        cumulativeSubscribersTableCalculationWorker.onMessage(({ data }) => {
          //1 Subscriber from Installs => subscriberFromInstall
          dispatch(
            replaceCumulativeSubscriberYears(
              "subscriberFromInstall",
              data?.subscriberFromInstalls
            )
          );
          //2 Subscription (install to journey) => subscriptionInstallToJourneys
          dispatch(
            replaceCumulativeSubscriberYears(
              "subscriptionInstallToJourneys",
              data?.subscriptionInstallToJourney
            )
          );
          //3 Subscriber from Journeys => subscriberFromJourney
          dispatch(
            replaceCumulativeSubscriberYears(
              "subscriberFromJourney",
              data?.subscriberFromJourneys
            )
          );
          //4 New Annual Subscribers => newAnnualSubscribers
          dispatch(
            replaceCumulativeSubscriberYears(
              "newAnnualSubscribers",
              data?.newAnnualSubscribers
            )
          );
          //5 Number of Annual Renewals => annualRenewals
          dispatch(
            replaceCumulativeSubscriberYears(
              "annualRenewals",
              data?.numberOfAnnualRenewals
            )
          );

          //6 Total Annual Subscribers => totalAnnualSubscribers
          dispatch(
            replaceCumulativeSubscriberYears(
              "totalAnnualSubscribers",
              data?.totalAnnualSubscribers
            )
          );

          //7 New Annual Subscribers from Renewals => newAnnualFromRenewals
          dispatch(
            replaceCumulativeSubscriberYears(
              "newAnnualFromRenewals",
              data?.newAnnualSubscribersFromRenewals
            )
          );
          //8 New Annual Subscribers from Installs => newAnnualFromInstalls
          dispatch(
            replaceCumulativeSubscriberYears(
              "newAnnualFromInstalls",
              data?.newAnnualSubscribersFromInstalls
            )
          );

          //9 Cumulative Subscribers => cumulativeSubscribers
          dispatch(
            replaceCumulativeSubscriberYears(
              "cumulativeSubscribers",
              data?.cumulativeSubscribersAmount
            )
          );
          // 4. New Monthly Subscribers => newMonthlySubscribers
          dispatch(
            replaceCumulativeSubscriberYears(
              "newMonthlySubscribers",
              data?.newMonthlySubscribers
            )
          );
          // 5. Number of Monthly Renewals => monthlyRenewals
          dispatch(
            replaceCumulativeSubscriberYears(
              "monthlyRenewals",
              data?.numberOfMonthlyRenewals
            )
          );
          // 6. Total Monthly Subscribers => totalMonthlySubscribers
          dispatch(
            replaceCumulativeSubscriberYears(
              "totalMonthlySubscribers",
              data?.totalMonthlySubscribers
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    periodicity,
    cumulativeInstalls["install"]?.months,
    subscriberAcquisitionAssumptionsState["subscription"]?.months,
    subscriberAcquisitionAssumptionsState["churnRate"]?.months,
    cumulativeSubscribers["annualToTotalSubscribers"]?.months,
    cumulativeSubscribers["journeyToSubscription"]?.months,
  ]);

  // CumulativeSubscribersTable end

  // __________________________________________________________________________

  // Revenue Tab start

  // Subscription Revenue Table start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorkers Subscription Revenue Table
      let sanitizedCumulativeSubscribers = JSON.parse(
        JSON.stringify(cumulativeSubscribers)
      );
      let sanitizedSubscriptionRevenue = JSON.parse(
        JSON.stringify(subscriptionRevenue)
      );
      let sanitizedOtherRevenue = JSON.parse(JSON.stringify(otherRevenue));

      (() => {
        debouncedLoading(dispatch, true);
        calculateSubscriptionRevenueTableWorker.postMessage(
          sanitizedCumulativeSubscribers,
          sanitizedSubscriptionRevenue,
          sanitizedOtherRevenue,
          monthCount
        );
        calculateSubscriptionRevenueTableWorker.onMessage(({ data }) => {
          dispatch(
            replaceSubscriptionRevenueByYear(
              "numberOfNewSubscribers",
              data?.numberOfNewSubscribers
            )
          );

          if (
            !isEqual(
              data?.subscriptionRevenueAmountMonths,
              subscriptionRevenue["subscriptionRevenue"]?.months
            )
          ) {
            dispatch(
              replaceSubscriptionRevenueByYear(
                "subscriptionRevenue",
                data?.subscriptionRevenueAmountMonths
              )
            );
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_subscriptionRevenue",
                data?.subscriptionRevenueAmountMonths
              )
            );
            // Revenue / total revenue

            // fcf_main_totalRevenues Revenues
            dispatch(
              replaceFreeCashFlowInitialYears(
                "fcf_main_totalRevenues",
                data?.pnl_main_totalRevenues
              )
            );
            // Growth
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_revenueGrowth",
                data?.pnl_main_revenueGrowth
              )
            );
            // YoY Growth (x)
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_yoyGrowth",
                data?.pnl_main_yoyGrowth
              )
            );
            debouncedLoading(dispatch, false);
          }
        });
      })();
    }
  }, [
    monthCount,
    cumulativeSubscribers["totalAnnualSubscribers"]?.months,
    cumulativeSubscribers["totalMonthlySusbcribers"]?.months,
    subscriptionRevenue["annualAverageFee"].months,
    otherRevenue["otherRevenue"]?.months,
    periodicity,
  ]);
  // Subscription Revenue Table end

  // Subscription Commissions start

  // __________________________________________________________________________

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorkers Subscription Commissions Table

      let sanitizedSubscriptionCommissions = JSON.parse(
        JSON.stringify(subscriptionCommissions)
      );

      let sanitizedSubscriptionRevenue = JSON.parse(
        JSON.stringify(subscriptionRevenue)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateSubscriptionCommissionsTableWorker.postMessage(
          sanitizedSubscriptionCommissions,
          sanitizedSubscriptionRevenue,
          monthCount
        );
        calculateSubscriptionCommissionsTableWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.appStoresTotalAmounts,
              subscriptionCommissions["appStoresTotal"]?.months
            )
          ) {
            dispatch(
              replaceSubscriptionCommissionByYears(
                "appStoresTotal",
                data?.appStoresTotalAmounts
              )
            );
          }
          dispatch(
            replaceSubscriptionCommissionByYears(
              "appleAppStoreCommissionAmount",
              data?.salesCommissionsUsdAppleAppStore
            )
          );

          dispatch(
            replaceSubscriptionCommissionByYears(
              "googlePlayAppStoreCommissionAmount",
              data?.salesCommissionsUsdGooglePlay
            )
          );
          dispatch(
            replaceSubscriptionCommissionByYears(
              "websiteStoreCommissionAmount",
              data?.salesCommissionsUsdWebsite
            )
          );

          dispatch(
            replaceSubscriptionCommissionByYears(
              "totalSalesCommissionsAmount",
              data?.totalSalesCommission
            )
          );
          const appStoreCommissionsPayload =
            companyType === GeneralCoType
              ? getZeroFilledMonths(monthCount)
              : data?.pnl_main_appStoreCommissions;
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_appStoreCommissions",
              appStoreCommissionsPayload
            )
          );
          dispatch(
            replaceSubscriptionCommissionByYears(
              "netSubscriptionRevenue",
              data?.netSubscriptionRevenue
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    subscriptionRevenue["subscriptionRevenue"]?.months,

    subscriptionCommissions["appleAppStore"]?.months,
    subscriptionCommissions["googlePlayAppStore"]?.months,
    subscriptionCommissions["websiteStore"]?.months,

    subscriptionCommissions["appleAppStoreCommission"]?.months,
    subscriptionCommissions["googlePlayAppStoreCommission"]?.months,
    subscriptionCommissions["websiteStoreCommission"]?.months,
    companyType,
  ]);

  // __________________________________________________________________________

  // Subscription Commissions end

  // Other Revenue start
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorkers Other Revenue
      let sanitizedOtherRevenue = JSON.parse(JSON.stringify(otherRevenue));
      let sanitizedSubscriptionRevenue = JSON.parse(
        JSON.stringify(subscriptionRevenue)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateOtherRevenuesWorker.postMessage(
          sanitizedOtherRevenue,
          sanitizedSubscriptionRevenue,
          monthCount
        );
        calculateOtherRevenuesWorker.onMessage(({ data }) => {
          // Other Revenues
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_otherRevenues",
              otherRevenue["otherRevenue"]?.months
            )
          );

          // Revenues
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_totalRevenues",
              data?.pnl_main_totalRevenues
            )
          );
          // fcf_main_totalRevenues Revenues
          dispatch(
            replaceFreeCashFlowInitialYears(
              "fcf_main_totalRevenues",
              data?.pnl_main_totalRevenues
            )
          );
          // Growth
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_revenueGrowth",
              data?.pnl_main_revenueGrowth
            )
          );
          // YoY Growth (x)
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_yoyGrowth",
              data?.pnl_main_yoyGrowth
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    otherRevenue["otherRevenue"]?.months,
    subscriptionRevenue["subscriptionRevenue"]?.months,
  ]);

  // Other Revenue end

  // Revenue Tab end

  // __________________________________________________________________________

  // Cost And Expenses Tab Start
  // Cost of Sales Table start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      let sanitizedCostOfSales = JSON.parse(JSON.stringify(costOfSales));

      (() => {
        debouncedLoading(dispatch, true);
        calculateContentAndProductionCostsWorker.postMessage(
          sanitizedCostOfSales,
          monthCount
        );
        calculateContentAndProductionCostsWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.contentProductionKey,
              costOfSales["contentProductionKey"]?.months
            )
          ) {
            dispatch(
              replaceCostOfSaleContdYears(
                "contentProductionKey",
                data?.contentProductionKey
              )
            );
          }
          const contentAndProductionPayload =
            companyType === GeneralCoType
              ? getZeroFilledMonths(monthCount)
              : data?.pnl_main_contentAndProductionCosts;
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_contentAndProductionCosts",
              contentAndProductionPayload
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    costOfSales["writtenContentKey"]?.months,
    costOfSales["studioKey"]?.months,
    costOfSales["dubbingArtistsKey"]?.months,
    costOfSales["musicKey"]?.months,
    companyType,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      let sanitizedCumulativeMembers = JSON.parse(
        JSON.stringify(cumulativeMembers)
      );
      let sanitizedCostOfSalesContd = JSON.parse(
        JSON.stringify(costOfSalesContd)
      );

      (() => {
        debouncedLoading(dispatch, true);
        serverCostAndNumberOfAvgCumulativeMembersCalculationWroker.postMessage(
          sanitizedCumulativeMembers,
          sanitizedCostOfSalesContd,
          monthCount
        );
        serverCostAndNumberOfAvgCumulativeMembersCalculationWroker.onMessage(
          ({ data }) => {
            if (
              !isEqual(
                data?.numberOfAvgCumulativeMembers,
                costOfSalesContd["numberOfAvgCumMembers"]?.months
              )
            ) {
              dispatch(
                replaceCostOfSaleContdYearsSecond(
                  "numberOfAvgCumMembers",
                  data?.numberOfAvgCumulativeMembers
                )
              );
            }

            if (
              !isEqual(
                data?.serverCostKey,
                costOfSalesContd["serverCostKey"]?.months
              )
            ) {
              dispatch(
                replaceCostOfSaleContdYearsSecond(
                  "serverCostKey",
                  data?.serverCostKey
                )
              );
            }
            const serverCostPayload =
              companyType === GeneralCoType
                ? getZeroFilledMonths(monthCount)
                : data?.pnl_main_serverCost;
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_serverCost",
                serverCostPayload
              )
            );
            debouncedLoading(dispatch, false);
          }
        );
      })();
    }
  }, [
    monthCount,
    periodicity,
    cumulativeMembers["total"]?.months,
    cumulativeMembers["cumulativeMembers"]?.months,
    costOfSalesContd["fixedCostKey"]?.months,
    // costOfSalesContd["numberOfAvgCumMembers"]?.years,
    costOfSalesContd["numberOfConcurrentKey"]?.months,
    costOfSalesContd["concurrentRatioKey"]?.months,
    costOfSalesContd["concurrentAmountKey"]?.months,
    companyType,
  ]);
  // Cost of Sales Table end

  // General Administrative Expenses Table start
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // Other Operating Expenses
      let sanitizedGeneralAdministrativeExpenses = JSON.parse(
        JSON.stringify(generalAdministrativeExpenses)
      );

      (() => {
        debouncedLoading(dispatch, true);
        GeneralAdministrativeExpensesTableCalculationWorker.postMessage(
          sanitizedGeneralAdministrativeExpenses,
          monthCount
        );
        GeneralAdministrativeExpensesTableCalculationWorker.onMessage(
          ({ data }) => {
            if (
              !isEqual(
                data?.gae_otherOperatingExpenses,
                generalAdministrativeExpenses["gae_otherOperatingExpenses"]
                  ?.months
              )
            ) {
              dispatch(
                replaceGeneralAdministrativeExpenseYears(
                  "gae_otherOperatingExpenses",
                  data?.gae_otherOperatingExpenses
                )
              );
              // Personnel Expenses
              dispatch(
                replaceProfitAndLossMainYears(
                  "pnl_main_otherOperatingExpenses",
                  data?.pnl_main_otherOperatingExpenses
                )
              );
            }
            debouncedLoading(dispatch, false);
          }
        );
      })();
    }
  }, [
    monthCount,
    periodicity,
    generalAdministrativeExpenses["gae_legalKey"]?.months,
    generalAdministrativeExpenses["gae_accountingCPA"]?.months,
    generalAdministrativeExpenses["gae_bookkeeperKey"]?.months,
    generalAdministrativeExpenses["gae_researchAnalysis"]?.months,
    generalAdministrativeExpenses["gae_consultants"]?.months,
    generalAdministrativeExpenses["gae_rentGeneralExpenses"]?.months,
    generalAdministrativeExpenses["gae_fixedAsset"]?.months,
    generalAdministrativeExpenses["gae_educationExpenses"]?.months,
    generalAdministrativeExpenses["gae_emailSubscriptions"]?.months,
  ]);
  // General Administrative Expenses Table end

  // Sales and Marketing Expenses => General Administrative Expenses Table start
  useEffect(() => {
    if (periodicity !== "Yearly") {
      // web worker of Marketing Expenses &  Media Buying
      let sanitizedSalesAndMarketingExpenses = JSON.parse(
        JSON.stringify(salesAndMarketingExpenses)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateMarketingExpensesAndMediaBuyingWorker.postMessage(
          sanitizedSalesAndMarketingExpenses,
          monthCount
        );
        calculateMarketingExpensesAndMediaBuyingWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.same_marketingExpenses,
              salesAndMarketingExpenses["same_marketingExpenses"]?.months
            )
          ) {
            dispatch(
              replaceSalesAndMarketingExpenseYears(
                "same_marketingExpenses",
                data?.same_marketingExpenses
              )
            );
            // Marketing Expenses / pnl_main_marketingExpenses
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_marketingExpenses",
                data?.pnl_main_marketingExpenses
              )
            );
          }
          if (
            !isEqual(
              data?.same_mediaBuying,
              salesAndMarketingExpenses["same_mediaBuying"]?.months
            )
          ) {
            dispatch(
              replaceSalesAndMarketingExpenseYears(
                "same_mediaBuying",
                data?.same_mediaBuying
              )
            );
          }
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    salesAndMarketingExpenses["same_ombaa"]?.months,
    salesAndMarketingExpenses["same_seo"]?.months,
    salesAndMarketingExpenses["same_mediaBuying"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // web worker of Marketing Expenses &  Media Buying
      let sanitizedAdvertisementExpenses = JSON.parse(
        JSON.stringify(advertisementExpenses)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculatePnl_main_advertisementExpensesWorker.postMessage(
          sanitizedAdvertisementExpenses,
          monthCount
        );
        calculatePnl_main_advertisementExpensesWorker.onMessage(({ data }) => {
          dispatch(
            replaceAdvertisementExpenseYears(
              "ae_advertisementExpenses",
              advertisementExpenses["ae_prOnOffInfluencerActivities"]?.months
            )
          );

          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_advertisementExpenses",
              data?.pnl_main_advertisementExpenses
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    advertisementExpenses["ae_prOnOffInfluencerActivities"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // web worker of Other Advertisement & Marketing Expenses => Total
      let sanitizedotherAdvertisementAndMarketingExpenses = JSON.parse(
        JSON.stringify(otherAdvertisementAndMarketingExpenses)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateOtherAdvertisementAndMarketingExpensesTotalWorker.postMessage(
          sanitizedotherAdvertisementAndMarketingExpenses,
          monthCount
        );
        calculateOtherAdvertisementAndMarketingExpensesTotalWorker.onMessage(
          ({ data }) => {
            dispatch(
              replaceOtherAdvertisementAndMarketingExpenseYears(
                "oame_otherAdvAndMarketing",
                data?.oame_otherAdvAndMarketing
              )
            );
            // Other Advertisement & Marketing Expenses / pnl_main_otherAdvMarketingExpenses
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_otherAdvMarketingExpenses",
                data?.pnl_main_otherAdvMarketingExpenses
              )
            );
            debouncedLoading(dispatch, false);
          }
        );
      })();
    }
  }, [
    monthCount,
    periodicity,
    otherAdvertisementAndMarketingExpenses["oame_attributionTool"]?.months,
    otherAdvertisementAndMarketingExpenses["oame_analyticsAndEngagementTool"]
      ?.months,
    otherAdvertisementAndMarketingExpenses["oame_mailing"]?.months,
    otherAdvertisementAndMarketingExpenses["oame_customerSupport"]?.months,
    otherAdvertisementAndMarketingExpenses["oame_sponsorship"]?.months,
    otherAdvertisementAndMarketingExpenses["oame_other"]?.months,
  ]);
  // Sales and Marketing Expenses => General Administrative Expenses Table end

  // Cost And Expenses Tab end

  // ___________________________________________________________________

  // Hiring Plan tab start

  // Founders and Partners start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      let sanitizeFoundersAndPartners = JSON.parse(
        JSON.stringify(foundersAndPartners)
      );
      debouncedLoading(dispatch, true);
      // webWorker of Founders and Partners
      (() => {
        calculateFoundersAndPartnersWorker.postMessage(
          sanitizeFoundersAndPartners,
          monthCount
        );
        calculateFoundersAndPartnersWorker.onMessage(({ data }) => {
          dispatch(
            replaceFoundersAndPartnersYears(
              "hp_fp_headCount",
              data?.hp_fp_headCount
            )
          );
          dispatch(
            replaceFoundersAndPartnersYears(
              "hp_fp_foundersAndPartnersHeader",
              data?.hp_fp_foundersAndPartnersHeader
            )
          );
        });
        debouncedLoading(dispatch, false);
      })();
    }
  }, [
    monthCount,
    periodicity,
    foundersAndPartners["hp_fp_founders"]?.months,
    foundersAndPartners["hp_fp_advisoryBoard"]?.months,
    foundersAndPartners["hp_fp_costPerHead"]?.months,
  ]);

  // Founders and Partners end

  // Tech & Product Development start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      let sanitizetechProductDev = JSON.parse(JSON.stringify(techProductDev));
      // webWorker of Founders and Partners
      (() => {
        debouncedLoading(dispatch, true);
        calculateTechAndDevelopmentWorker.postMessage(
          sanitizetechProductDev,
          monthCount
        );

        calculateTechAndDevelopmentWorker.onMessage(({ data }) => {
          dispatch(
            replaceTechProductDevYears(
              "hp_tpd_headCount",
              data?.hp_tpd_headCount
            )
          );
          dispatch(
            replaceTechProductDevYears("hp_tpd_header", data?.hp_tpd_header)
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    techProductDev["hp_tpd_engineer"]?.months,
    techProductDev["hp_tpd_developer"]?.months,
    techProductDev["hp_tpd_uiuxDesigner"]?.months,
    techProductDev["hp_tpd_productManagement"]?.months,
    techProductDev["hp_tpd_productManagement"]?.months,
    techProductDev["hp_tpd_costPerHead"]?.months,
  ]);

  // Tech & Product Development end

  // Sales and Marketing Teams start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      let sanitizeSalesAndMarketing = JSON.parse(
        JSON.stringify(salesAndMarketing)
      );
      // webWorker of Founders and Partners
      (() => {
        debouncedLoading(dispatch, true);
        calculateSalesAndMarketingTeamsWorker.postMessage(
          sanitizeSalesAndMarketing,
          monthCount
        );
        calculateSalesAndMarketingTeamsWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.hp_sm_headCount,
              salesAndMarketing["hp_sm_headCount"]?.months
            )
          ) {
            dispatch(
              replaceSalesMarketingYears(
                "hp_sm_headCount",
                data?.hp_sm_headCount
              )
            );
          }

          if (
            !isEqual(
              data?.hp_sm_salesAndMarketingHeader,
              salesAndMarketing["hp_sm_salesAndMarketingHeader"]?.months
            )
          ) {
            dispatch(
              replaceSalesMarketingYears(
                "hp_sm_salesAndMarketingHeader",
                data?.hp_sm_salesAndMarketingHeader
              )
            );
            dispatch(
              replaceHiringPlanAverageYears(
                "hp_avg_salesAndMarketingExpenses",
                data?.hp_sm_salesAndMarketingHeader
              )
            );

            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_personnelExpenses2",
                data?.pnl_main_personnelExpenses2
              )
            );
          }
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    salesAndMarketing["hp_sm_salesTeamKey"]?.months,
    salesAndMarketing["hp_sm_marketingTeam"]?.months,
    salesAndMarketing["hp_sm_businessDevelopment"]?.months,
    salesAndMarketing["hp_sm_userAcquisition"]?.months,
    salesAndMarketing["hp_sm_crm"]?.months,
    salesAndMarketing["hp_sm_subscriberSupport"]?.months,
    salesAndMarketing["hp_sm_socialMediaAndContentDesign"]?.months,
    salesAndMarketing["hp_sm_communityManagement"]?.months,
    salesAndMarketing["hp_sm_costPerHead"]?.months,
  ]);

  // Sales and Marketing Teams end

  // Finance & Analytics & Operations start

  // calculateFinanceAndAnalyticsAndOperationsWorker

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Finance & Analytics & Operations
      let sanitizeFinanceAndAnalytics = JSON.parse(
        JSON.stringify(financeAndAnalytics)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateFinanceAndAnalyticsAndOperationsWorker.postMessage(
          sanitizeFinanceAndAnalytics,
          monthCount
        );
        calculateFinanceAndAnalyticsAndOperationsWorker.onMessage(
          ({ data }) => {
            if (
              !isEqual(
                data?.hp_fa_headCount,
                financeAndAnalytics["hp_fa_headCount"]?.months
              )
            ) {
              dispatch(
                replaceFinanceAnalyticsYears(
                  "hp_fa_headCount",
                  data?.hp_fa_headCount
                )
              );
            }

            if (
              !isEqual(
                data?.hp_fa_financeAndAnalytics,
                financeAndAnalytics["hp_fa_financeAndAnalytics"]?.months
              )
            ) {
              dispatch(
                replaceFinanceAnalyticsYears(
                  "hp_fa_financeAndAnalytics",
                  data?.hp_fa_financeAndAnalytics
                )
              );
            }
            debouncedLoading(dispatch, false);
          }
        );
      })();
    }
  }, [
    monthCount,
    periodicity,
    financeAndAnalytics["hp_fa_financeTeam"]?.months,
    financeAndAnalytics["hp_fa_dataScienceAnalyticsTeam"]?.months,
    financeAndAnalytics["hp_fa_hrTeam"]?.months,
    financeAndAnalytics["hp_fa_legalTeam"]?.months,
    financeAndAnalytics["hp_fa_costPerHead"]?.months,
  ]);

  // Finance & Analytics & Operations end

  // other start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker other
      let sanitizeOther = JSON.parse(JSON.stringify(other));

      (() => {
        debouncedLoading(dispatch, true);
        otherTableCalculationWorker.postMessage(sanitizeOther, monthCount);
        otherTableCalculationWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.hp_other_headCount,
              other["hp_other_headCount"]?.months
            )
          ) {
            dispatch(
              replaceHiringPlanOtherYears(
                "hp_other_headCount",
                data?.hp_other_headCount
              )
            );
          }

          if (
            !isEqual(data?.hp_other_header, other["hp_other_header"]?.months)
          ) {
            dispatch(
              replaceHiringPlanOtherYears(
                "hp_other_header",
                data?.hp_other_header
              )
            );
          }
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    other["hp_other_freelancers"]?.months,
    other["hp_other_costPerHead"]?.months,
  ]);

  // other end

  // Average start

  // calculateTotalHeadCountWorker

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Total Head Count
      let sanitizeFinanceAndAnalytics = JSON.parse(
        JSON.stringify(financeAndAnalytics)
      );
      let sanitizeFoundersAndPartners = JSON.parse(
        JSON.stringify(foundersAndPartners)
      );
      let sanitizeSalesAndMarketing = JSON.parse(
        JSON.stringify(salesAndMarketing)
      );
      let sanitizeOther = JSON.parse(JSON.stringify(other));
      let sanitizeTechAndProduct = JSON.parse(JSON.stringify(techAndProduct));

      (() => {
        debouncedLoading(dispatch, true);
        calculateTotalHeadCountWorker.postMessage(
          sanitizeFinanceAndAnalytics,
          sanitizeFoundersAndPartners,
          sanitizeOther,
          sanitizeSalesAndMarketing,
          sanitizeTechAndProduct,
          monthCount
        );
        calculateTotalHeadCountWorker.onMessage(({ data }) => {
          // if (
          //   !isEqual(hp_avg_totalHeadCount, average["hp_avg_totalHeadCount"]?.years)
          // ) {
          dispatch(
            replaceHiringPlanAverageYears(
              "hp_avg_totalHeadCount",
              data?.hp_avg_totalHeadCount
            )
          );
          // }
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    financeAndAnalytics["hp_fa_headCount"]?.months,
    foundersAndPartners["hp_fp_headCount"]?.months,
    other["hp_other_headCount"]?.months,
    salesAndMarketing["hp_sm_headCount"]?.months,
    techAndProduct["hp_tpd_headCount"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Total Cost
      let sanitizeFinanceAndAnalytics = JSON.parse(
        JSON.stringify(financeAndAnalytics)
      );
      let sanitizeFoundersAndPartners = JSON.parse(
        JSON.stringify(foundersAndPartners)
      );
      let sanitizeSalesAndMarketing = JSON.parse(
        JSON.stringify(salesAndMarketing)
      );
      let sanitizeOther = JSON.parse(JSON.stringify(other));
      let sanitizeTechAndProduct = JSON.parse(JSON.stringify(techAndProduct));

      (() => {
        debouncedLoading(dispatch, true);
        calculateTotalCostWorker.postMessage(
          sanitizeFinanceAndAnalytics,
          sanitizeFoundersAndPartners,
          sanitizeOther,
          sanitizeSalesAndMarketing,
          sanitizeTechAndProduct,
          monthCount
        );
        calculateTotalCostWorker.onMessage(({ data }) => {
          dispatch(
            replaceHiringPlanAverageYears(
              "hp_avg_totalCost",
              data?.hp_avg_totalCost
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    financeAndAnalytics["hp_fa_financeAndAnalytics"]?.months,
    foundersAndPartners["hp_fp_foundersAndPartnersHeader"]?.months,
    other["hp_other_header"]?.months,
    salesAndMarketing["hp_sm_salesAndMarketingHeader"]?.months,
    techAndProduct["hp_tpd_header"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Salary per Head Count
      let sanitizeAverage = JSON.parse(JSON.stringify(average));
      (() => {
        debouncedLoading(dispatch, true);
        calculateAverageSalaryPerHeadCountWorker.postMessage(
          sanitizeAverage,
          monthCount
        );
        calculateAverageSalaryPerHeadCountWorker.onMessage(({ data }) => {
          dispatch(
            replaceHiringPlanAverageYears(
              "hp_avg_avgSalary",
              data?.hp_avg_avgSalary
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    average["hp_avg_totalCost"]?.months,
    average["hp_avg_totalHeadCount"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker General Administrative Expenses
      let sanitizeAverage = JSON.parse(JSON.stringify(average));
      (() => {
        debouncedLoading(dispatch, true);
        calculateGeneralAdministrativeExpensestWorker.postMessage(
          sanitizeAverage,
          monthCount
        );
        calculateGeneralAdministrativeExpensestWorker.onMessage(({ data }) => {
          if (
            !isEqual(
              data?.hp_avg_genAdminExpenses,
              average["hp_avg_genAdminExpenses"]?.months
            )
          ) {
            dispatch(
              replaceHiringPlanAverageYears(
                "hp_avg_genAdminExpenses",
                data?.hp_avg_genAdminExpenses
              )
            );
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_personnelExpenses",
                data?.pnl_main_personnelExpenses
              )
            );
          }
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    average["hp_avg_totalCost"]?.months,
    average["hp_avg_salesAndMarketingExpenses"]?.months,
  ]);

  // Average end

  // Hiring Plan tab end

  // Profit & Loss start

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Gross Profit,Gross Profit Margin
      let sanitizeProfitAndLossMain = JSON.parse(
        JSON.stringify(profitAndLossMain)
      );
      (() => {
        debouncedLoading(dispatch, true);
        calculateGrossProfitAndMarginWorker.postMessage(
          sanitizeProfitAndLossMain,
          monthCount
        );
        calculateGrossProfitAndMarginWorker.onMessage(({ data }) => {
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_grossProfit",
              data?.pnl_main_grossProfit
            )
          );
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_grossProfitMargin",
              data?.pnl_main_grossProfitMargin
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    profitAndLossMain["pnl_main_totalRevenues"]?.months,
    profitAndLossMain["pnl_main_appStoreCommissions"]?.months,
    profitAndLossMain["pnl_main_contentAndProductionCosts"]?.months,
    profitAndLossMain["pnl_main_serverCost"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker General Administrative Expense
      let sanitizeProfitAndLossMain = JSON.parse(
        JSON.stringify(profitAndLossMain)
      );
      (() => {
        debouncedLoading(dispatch, true);
        calculateGeneralAdministrativeExpenseWorker.postMessage(
          sanitizeProfitAndLossMain,
          monthCount
        );
        calculateGeneralAdministrativeExpenseWorker.onMessage(({ data }) => {
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_genAdminExpenses",
              data?.pnl_main_genAdminExpenses
            )
          );
          dispatch(
            replaceFreeCashFlowInitialYears(
              "fcf_main_generalAdministrativeExpenses",
              data?.pnl_main_genAdminExpenses
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    profitAndLossMain["pnl_main_personnelExpenses"]?.months,
    profitAndLossMain["pnl_main_otherOperatingExpenses"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker Sales & Marketing Expenses
      let sanitizeProfitAndLossMain = JSON.parse(
        JSON.stringify(profitAndLossMain)
      );
      (() => {
        debouncedLoading(dispatch, true);
        calculateSalesAndMarketingExpensesWorker.postMessage(
          sanitizeProfitAndLossMain,
          monthCount
        );
        calculateSalesAndMarketingExpensesWorker.onMessage(({ data }) => {
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_sAndMExpenses",
              data?.pnl_main_sAndMExpenses
            )
          );
          dispatch(
            replaceFreeCashFlowInitialYears(
              "fcf_main_salesAndMarketingExpenses",
              data?.pnl_main_sAndMExpenses
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    profitAndLossMain["pnl_main_personnelExpenses2"]?.months,
    profitAndLossMain["pnl_main_marketingExpenses"]?.months,
    profitAndLossMain["pnl_main_advertisementExpenses"]?.months,
    profitAndLossMain["pnl_main_otherAdvMarketingExpenses"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker  EBITDA / pnl_main_ebitda
      let sanitizeProfitAndLossMain = JSON.parse(
        JSON.stringify(profitAndLossMain)
      );
      (() => {
        debouncedLoading(dispatch, true);
        calculateEBITDAPnl_main_ebitdaWorker.postMessage(
          sanitizeProfitAndLossMain,
          monthCount
        );
        calculateEBITDAPnl_main_ebitdaWorker.onMessage(({ data }) => {
          dispatch(
            replaceProfitAndLossMainYears(
              "pnl_main_ebitda",
              data?.pnl_main_ebitda
            )
          );
          debouncedLoading(dispatch, false);
        });
      })();
    }
  }, [
    monthCount,
    periodicity,
    profitAndLossMain["pnl_main_sAndMExpenses"]?.months,
    profitAndLossMain["pnl_main_grossProfit"]?.months,
    profitAndLossMain["pnl_main_genAdminExpenses"]?.months,
  ]);

  useEffect(() => {
    if (periodicity !== "Yearly") {
      // webWorker EBITDA Margin / pnl_main_ebitdaMargin
      let sanitizeProfitAndLossMain = JSON.parse(
        JSON.stringify(profitAndLossMain)
      );

      (() => {
        debouncedLoading(dispatch, true);
        calculateEBITDAMargin_pnl_main_ebitdaMarginWorker.postMessage(
          sanitizeProfitAndLossMain,
          monthCount
        );
        calculateEBITDAMargin_pnl_main_ebitdaMarginWorker.onMessage(
          ({ data }) => {
            dispatch(
              replaceProfitAndLossMainYears(
                "pnl_main_ebitdaMargin",
                data?.pnl_main_ebitdaMargin
              )
            );
            debouncedLoading(dispatch, false);
          }
        );
      })();
    }
  }, [
    monthCount,
    periodicity,
    profitAndLossMain["pnl_main_ebitda"]?.months,
    profitAndLossMain["pnl_main_totalRevenues"]?.months,
  ]);
  // Profit & Loss end

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          // Top bar (horizontal) background - Logo and etc.
          bgcolor: theme.palette.background.default,
          transition: leftDrawerOpened
            ? theme.transitions.create("width")
            : "none",
          boxShadow: "0px 0px 24px 0px rgba(0, 0, 0, 0.10)",
          zIndex: 9999,
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Box sx={{ display: "flex" }}>
        <Sidebar />
        <MobileDrawer
          drawerOpen={leftDrawerOpened}
          drawerToggle={handleLeftDrawerToggle}
        />

        {/* main content */}
        <Main theme={theme} open={leftDrawerOpened}>
          {/* breadcrumb */}
          <Outlet />
        </Main>
      </Box>
      {centralLoader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            position: "absolute",
            height: "100vh",
            width: "100vw",
            background: "rgba(0,0,0,0.3)",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default MainLayout;
